/**
 * 对话式配置修改面板（B1a）。
 *
 * 数据源：POST /agents/{id}/config-proposals（instruction + history → 有界配置变更提案）。
 * 应用：POST /agents/{id}/config-proposals/apply（用户勾选的 op 子集）。
 *
 * - 多轮对话输入：history 随请求携带（前端保存的对话记录），支持迭代追问/修正；
 * - 提案 op 按维度（人设 / 技能 / 工作流）分组，每项用 ConfigDiffViewer 展示
 *   before/after diff 与变更理由；assumptions 与 rejected（越界拒绝说明）如实展示；
 * - 勾选 → 应用 → 逐项结果反馈（applied / skipped + 原因），成功后回调 onApplied
 *   让配置中心刷新数据；
 * - 失败诚实降级（错误提示 + 重试），不阻塞配置中心其他功能。
 */
import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent, KeyboardEvent } from 'react';
import { X, MessageSquare, Loader2, AlertTriangle, Send } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from './ui/toastContext';
import ConfigDiffViewer from './ConfigDiffViewer';

interface ProposalOp {
  dimension: 'persona' | 'skill' | 'workflow';
  target: string;
  field: string;
  before: unknown;
  after: unknown;
  reason: string;
}

interface Proposal {
  ops: ProposalOp[];
  assumptions: string[];
  rejected: string;
}

interface ApplyResultItem {
  dimension: string;
  target: string;
  field: string;
  status: 'applied' | 'skipped';
  reason: string;
}

interface ApplyResponse {
  agent_id: string;
  profile_id: string;
  applied_count: number;
  skipped_count: number;
  results: ApplyResultItem[];
}

interface Turn {
  role: 'user' | 'assistant';
  content: string;
}

interface ConfigChangePanelProps {
  agentId: string;
  open: boolean;
  onClose: () => void;
  /** 应用成功且有落库时回调（配置中心刷新子资源） */
  onApplied?: () => void;
}

const DIMENSION_LABELS: Record<string, string> = {
  persona: '人设',
  skill: '技能',
  workflow: '工作流',
};

const bodyStyle: CSSProperties = { flex: 1, overflowY: 'auto', padding: 20 };
const sectionTitleStyle: CSSProperties = { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' };
const itemStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  padding: '10px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  marginBottom: 8,
  background: 'var(--bg-primary)',
};
const turnBubbleStyle: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-md)',
  marginBottom: 8,
  fontSize: 13,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const stringify = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2);

/** op 字段的中文可读标签 */
const fieldLabel = (op: ProposalOp): string => {
  if (op.dimension === 'persona') return '内容';
  if (op.dimension === 'workflow' && op.field !== 'description') return `状态「${op.field}」描述`;
  if (op.field === 'description') return '描述';
  if (op.field === 'instructions') return '操作指引';
  return op.field;
};

/** 助手轮次摘要（供下一轮迭代追问时作为历史上下文） */
const summarizeProposal = (p: Proposal): string => {
  if (p.rejected) return `提案被拒绝：${p.rejected}`;
  const parts = p.ops.slice(0, 8).map(
    (op) => `${DIMENSION_LABELS[op.dimension] || op.dimension}「${op.target}」${fieldLabel(op)} → ${stringify(op.after)}`,
  );
  const rest = p.ops.length > 8 ? `；…等共 ${p.ops.length} 项` : '';
  return `已生成 ${p.ops.length} 项配置变更提案：${parts.join('；')}${rest}`;
};

export default function ConfigChangePanel({
  agentId,
  open,
  onClose,
  onApplied,
}: ConfigChangePanelProps) {
  const toast = useToast();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<ApplyResponse | null>(null);

  const grouped = useMemo(() => {
    const map: Record<string, ProposalOp[]> = {};
    (proposal?.ops || []).forEach((op) => {
      (map[op.dimension] ||= []).push(op);
    });
    return map;
  }, [proposal]);

  const hasSelection = checked.size > 0;

  const send = useCallback(async () => {
    const instruction = input.trim();
    if (!instruction || loading || applying) return;
    setLoading(true);
    setError('');
    setProposal(null);
    setChecked(new Set());
    setApplyResult(null);
    const history = turns.map((t) => ({ role: t.role, content: t.content }));
    try {
      const res = await apiClient.post(`/agents/${agentId}/config-proposals`, { instruction, history });
      const data = res.data as Proposal;
      setProposal(data);
      setTurns((prev) => [
        ...prev,
        { role: 'user', content: instruction },
        { role: 'assistant', content: summarizeProposal(data).slice(0, 2000) },
      ]);
      setInput('');
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' && detail ? detail : '生成提案失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [agentId, input, loading, applying, turns]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };

  const toggle = (idx: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const applySelected = async () => {
    if (!proposal || !hasSelection || applying) return;
    setApplying(true);
    setError('');
    const ops = proposal.ops.filter((_, i) => checked.has(i));
    try {
      const res = await apiClient.post(`/agents/${agentId}/config-proposals/apply`, { ops });
      const data = res.data as ApplyResponse;
      setApplyResult(data);
      toast.success(
        `已应用 ${data.applied_count} 项配置变更` + (data.skipped_count ? `，跳过 ${data.skipped_count} 项` : ''),
      );
      if (data.applied_count > 0) onApplied?.();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('应用配置变更失败' + (typeof detail === 'string' && detail ? `：${detail}` : ''));
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 150 }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: 640,
          height: '100dvh',
          background: 'var(--bg-secondary)',
          borderLeft: '1px solid var(--border-default)',
          zIndex: 160,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-default)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={18} color="var(--color-primary)" />
            <h3 style={{ margin: 0, fontSize: 16 }}>对话修改</h3>
          </div>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div style={bodyStyle}>
          {/* 多轮对话记录 */}
          {turns.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={sectionTitleStyle}>对话</h4>
              {turns.map((turn, i) => (
                <div
                  key={`t-${i}`}
                  style={{
                    ...turnBubbleStyle,
                    background: turn.role === 'user' ? 'rgba(88, 166, 255, 0.12)' : 'var(--bg-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                >
                  <div className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>
                    {turn.role === 'user' ? '我' : '助手'}
                  </div>
                  {turn.content}
                </div>
              ))}
            </div>
          )}

          {/* 输入区 */}
          <form onSubmit={onSubmit}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="用自然语言描述想改什么，例如：语气更亲切一点，回复前先确认订单状态；可连续追问修正…"
              rows={3}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-primary)',
                color: 'inherit',
                fontSize: 13,
              }}
            />
            <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
              <span className="text-muted" style={{ fontSize: 12 }}>
                回车发送，Shift+Enter 换行
              </span>
              <button className="primary" type="submit" disabled={!input.trim() || loading || applying}>
                {loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> 生成中…
                  </>
                ) : (
                  <>
                    <Send size={14} /> 生成提案
                  </>
                )}
              </button>
            </div>
          </form>

          {!loading && error && (
            <div className="card" style={{ marginTop: 16, borderColor: 'var(--color-error)' }}>
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} color="var(--color-warning)" />
                <span style={{ fontSize: 13 }}>{error}</span>
              </div>
            </div>
          )}

          {!loading && proposal && proposal.rejected && (
            <div className="card" style={{ marginTop: 16, borderColor: 'var(--color-warning)' }}>
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} color="var(--color-warning)" />
                <span style={{ fontSize: 13 }}>{proposal.rejected}</span>
              </div>
            </div>
          )}

          {!loading && proposal && !proposal.rejected && (
            <div style={{ marginTop: 16 }}>
              <h4 style={sectionTitleStyle}>
                变更提案（{proposal.ops.length} 项）— 勾选后点击「应用选中」
              </h4>
              {proposal.assumptions.length > 0 && (
                <div className="flex flex-wrap gap-2" style={{ marginBottom: 12 }}>
                  {proposal.assumptions.map((a, i) => (
                    <span key={`a-${i}`} className="badge info" style={{ fontSize: 12 }}>
                      假设：{a}
                    </span>
                  ))}
                </div>
              )}
              {Object.keys(grouped).map((dimension) => (
                <div key={dimension} style={{ marginBottom: 16 }}>
                  <h4 style={sectionTitleStyle}>
                    {DIMENSION_LABELS[dimension] || dimension}（{grouped[dimension].length}）
                  </h4>
                  {grouped[dimension].map((op, idx) => {
                    const globalIdx = proposal.ops.indexOf(op);
                    return (
                      <div key={`op-${dimension}-${idx}`} style={itemStyle}>
                        <input
                          type="checkbox"
                          data-testid={`op-check-${globalIdx}`}
                          checked={checked.has(globalIdx)}
                          onChange={() => toggle(globalIdx)}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, marginBottom: 4 }}>
                            {DIMENSION_LABELS[op.dimension]}「{op.target}」· {fieldLabel(op)}
                          </div>
                          <ConfigDiffViewer left={op.before ?? ''} right={op.after ?? ''} />
                          {op.reason && (
                            <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                              理由：{op.reason}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {!loading && proposal && !proposal.rejected && proposal.ops.length === 0 && (
            <p className="text-muted" style={{ marginTop: 16, fontSize: 13 }}>
              未提出任何白名单范围内的配置变更，可调整诉求后重试。
            </p>
          )}

          {/* 应用结果反馈 */}
          {applyResult && (
            <div className="card" style={{ marginTop: 16 }}>
              <h4 style={sectionTitleStyle}>
                应用结果：成功 {applyResult.applied_count} 项，跳过 {applyResult.skipped_count} 项
              </h4>
              {applyResult.results.map((r, i) => (
                <div key={`r-${i}`} style={{ fontSize: 13, marginBottom: 6 }}>
                  <span className={`badge ${r.status === 'applied' ? 'success' : 'warning'}`} style={{ marginRight: 8 }}>
                    {r.status === 'applied' ? '已应用' : '已跳过'}
                  </span>
                  {DIMENSION_LABELS[r.dimension] || r.dimension}「{r.target}」· {r.field}
                  <span className="text-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                    {r.reason}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-default)',
            display: 'flex',
            gap: 12,
          }}
        >
          <button className="secondary" style={{ flex: 1 }} onClick={onClose}>
            关闭
          </button>
          <button
            className="primary"
            style={{ flex: 2 }}
            disabled={!hasSelection || applying || loading}
            onClick={() => void applySelected()}
          >
            {applying ? (
              <>
                <Loader2 size={14} className="animate-spin" /> 应用中…
              </>
            ) : (
              <>
                <MessageSquare size={14} /> 应用选中
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
